import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Loader2, Eye, EyeOff } from "lucide-react";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultMode?: 'login' | 'signup';
}

export const AuthModal = ({ isOpen, onClose, defaultMode = 'login' }: AuthModalProps) => {
  const [mode, setMode] = useState<'login' | 'signup' | 'reset'>(defaultMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { signIn, signUp, resetPassword } = useAuth();
  const { t } = useLanguage();

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setMessage('');
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateForm = () => {
    if (!email) {
      setError(t('auth.errors.emailRequired'));
      return false;
    }
    
    if (!email.includes('@')) {
      setError(t('auth.errors.emailInvalid'));
      return false;
    }

    if (mode !== 'reset') {
      if (!password) {
        setError(t('auth.errors.passwordRequired'));
        return false;
      }
      
      if (password.length < 6) {
        setError(t('auth.errors.passwordTooShort'));
        return false;
      }

      if (mode === 'signup' && password !== confirmPassword) {
        setError(t('auth.errors.passwordsDoNotMatch'));
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!validateForm()) return;

    if (process.env.NODE_ENV === 'development') {
      console.log(`🔐 Starting ${mode} process for email:`, email);
    }
    setIsLoading(true);

    try {
      if (mode === 'login') {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Attempting login...');
        }
        const { error } = await signIn(email, password);
        if (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('❌ Login failed:', error.message);
          }
          if (error.message.includes('Supabase is not configured')) {
            setError('Authentication is currently being set up. Please try again later.');
          } else if (error.message === 'Invalid login credentials') {
            setError(t('auth.errors.invalidCredentials'));
          } else {
            setError(error.message);
          }
        } else {
          handleClose();
        }
      } else if (mode === 'signup') {
        const { error } = await signUp(email, password);
        if (error) {
          if (error.message.includes('Supabase is not configured')) {
            setError('Authentication is currently being set up. Please try again later.');
          } else {
            setError(error.message);
          }
        } else {
          setMessage(t('auth.messages.checkEmail'));
          setMode('login');
        }
      } else if (mode === 'reset') {
        const { error } = await resetPassword(email);
        if (error) {
          if (error.message.includes('Supabase is not configured')) {
            setError('Authentication is currently being set up. Please try again later.');
          } else {
            setError(error.message);
          }
        } else {
          setMessage(t('auth.messages.resetPasswordSent'));
        }
      }
    } catch (err) {
      setError(t('auth.errors.unexpected'));
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = (newMode: 'login' | 'signup' | 'reset') => {
    setMode(newMode);
    setError('');
    setMessage('');
  };

  const getTitle = () => {
    switch (mode) {
      case 'login': return t('auth.login.title');
      case 'signup': return t('auth.signup.title');
      case 'reset': return t('auth.reset.title');
    }
  };

  const getSubmitText = () => {
    switch (mode) {
      case 'login': return t('auth.login.submit');
      case 'signup': return t('auth.signup.submit');
      case 'reset': return t('auth.reset.submit');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold gradient-text">
            {getTitle()}
          </DialogTitle>
          <DialogDescription className="text-center text-muted-foreground">
            {mode === 'login' && 'Sign in to your account to continue'}
            {mode === 'signup' && 'Create a new account to get started'}
            {mode === 'reset' && 'Enter your email to reset your password'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
          )}

          {message && (
            <Alert className="border-green-200 bg-green-50">
              <AlertDescription className="text-green-800">{message}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">{t('auth.fields.email')}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.placeholders.email')}
              disabled={isLoading}
              required
            />
          </div>

          {mode !== 'reset' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.fields.password')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.placeholders.password')}
                    disabled={isLoading}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {mode === 'signup' && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('auth.fields.confirmPassword')}</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t('auth.placeholders.confirmPassword')}
                      disabled={isLoading}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          <Button type="submit" className="w-full hero-button" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {getSubmitText()}
          </Button>

          <Separator />

          <div className="space-y-2 text-center text-sm">
            {mode === 'login' && (
              <>
                <div className="text-center text-sm text-muted-foreground">
                  Need an account? Contact your administrator.
                </div>
                <div>
                  <Button
                    type="button"
                    variant="link"
                    className="p-0 h-auto text-sm text-muted-foreground"
                    onClick={() => switchMode('reset')}
                    disabled={isLoading}
                  >
                    {t('auth.login.forgotPassword')}
                  </Button>
                </div>
              </>
            )}

            {mode === 'signup' && (
              <div>
                {t('auth.signup.hasAccount')}{' '}
                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto font-semibold text-primary"
                  onClick={() => switchMode('login')}
                  disabled={isLoading}
                >
                  {t('auth.signup.loginLink')}
                </Button>
              </div>
            )}

            {mode === 'reset' && (
              <div>
                {t('auth.reset.backToLogin')}{' '}
                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto font-semibold text-primary"
                  onClick={() => switchMode('login')}
                  disabled={isLoading}
                >
                  {t('auth.reset.loginLink')}
                </Button>
              </div>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};